import { useCallback, useEffect, useRef, type KeyboardEvent } from 'react';
import { ChevronRight, FileText, Heading, List, Pencil, PenLine, Type } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReviewBlock, ReviewOutlineNode } from './adapter/types';
import { getBlockLabel } from './utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui-kit/collapsible';

const ICONS = {
  file: FileText,
  heading: Heading,
  signature: PenLine,
  list: List,
  text: Type,
} as const;

/** Truncate text to maxLen characters, appending ellipsis if needed. */
function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + '…';
}

/**
 * Every block id under a node. Prefers the explicit `blockIds` a builder
 * supplies, since a group's content set is a property of the document rather
 * than of how the tree chose to render it; falls back to walking children.
 */
function collectBlockIds(node: ReviewOutlineNode): string[] {
  if (node.blockIds) return node.blockIds;
  if (node.kind === 'block') return node.block ? [node.block.id] : [];
  return (node.children ?? []).flatMap(collectBlockIds);
}

function LeafItem({
  block,
  isEdited,
  isActive,
  onEdit,
}: {
  block: ReviewBlock;
  isEdited: boolean;
  isActive: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  const { label: typeLabel, Icon } = getBlockLabel(t, block.kind, block.isTitle);
  const label = block.label ?? typeLabel;
  // Blocks carrying an explicit label show it as a prefix, so e.g. an empty
  // signatory email reads "Email — (empty)" rather than as a blank row.
  const preview = block.text.trim()
    ? truncate(block.text, 80)
    : t('GENERATEREVIEW_OUTLINE_EMPTYVALUE', { defaultValue: '(empty)' });

  return (
    <button
      onClick={onEdit}
      className={`group w-full text-left flex items-start gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue ${
        isActive ? 'bg-selise-blue/8 border-l-2 border-selise-blue' : ''
      }`}
      aria-label={`${label}: ${truncate(block.text, 60)}`}
    >
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-globe-grey" />
      <span className="flex-1 min-w-0 text-xs leading-relaxed text-[hsl(var(--fg))]">
        {block.label && (
          <span className="block text-[10px] font-semibold uppercase tracking-wide text-globe-grey/80">
            {label}
          </span>
        )}
        {preview}
      </span>
      <span className="shrink-0 flex items-center gap-1.5">
        {isEdited && (
          <span
            className="h-2 w-2 rounded-full bg-selise-blue-500"
            title={t('GENERATEREVIEW_OUTLINE_EDITED', { defaultValue: 'Edited' })}
            aria-label={t('GENERATEREVIEW_OUTLINE_EDITED', { defaultValue: 'Edited' })}
          />
        )}
        <Pencil className="w-3 h-3 text-globe-grey opacity-0 group-hover:opacity-100 transition-opacity" />
      </span>
    </button>
  );
}

function GroupNode({
  node,
  editedBlockIds,
  activeBlockId,
  onEditBlock,
  openSections,
  onToggleSection,
}: {
  node: ReviewOutlineNode;
  editedBlockIds: Set<string>;
  activeBlockId?: string | null;
  onEditBlock: (block: ReviewBlock) => void;
  openSections: Record<string, boolean>;
  onToggleSection: (id: string, open: boolean) => void;
}) {
  const open = openSections[node.id] ?? node.defaultOpen ?? false;
  const isVirtual = node.variant === 'virtual';
  const Icon = node.icon ? ICONS[node.icon] : null;

  const blockIds = collectBlockIds(node);
  const editedCount = isVirtual ? 0 : blockIds.filter((id) => editedBlockIds.has(id)).length;

  // Auto-expand when the block being edited lives somewhere inside this group.
  const containsActive = !!activeBlockId && blockIds.includes(activeBlockId);
  useEffect(() => {
    if (containsActive) onToggleSection(node.id, true);
  }, [containsActive, node.id, onToggleSection]);

  return (
    <Collapsible open={open} onOpenChange={(o) => onToggleSection(node.id, o)}>
      <CollapsibleTrigger className="group w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selise-blue">
        <ChevronRight
          className={`w-3.5 h-3.5 shrink-0 text-globe-grey transition-transform duration-150 ${
            open ? 'rotate-90' : ''
          }`}
        />
        {Icon && <Icon className="w-3.5 h-3.5 shrink-0 text-selise-blue" />}
        <span className="flex-1 min-w-0 text-xs font-semibold text-[hsl(var(--fg))] truncate">
          {node.title}
        </span>
        {editedCount > 0 && (
          <span className="text-[10px] flex items-center justify-center size-4 font-medium text-selise-blue-500 bg-selise-blue-500/15 rounded-full px-1.5 py-0.5">
            {editedCount}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-4 border-l border-border pl-2 space-y-0.5 mt-0.5">
          {(node.children ?? []).map((child) => (
            <OutlineNode
              key={child.id}
              node={child}
              editedBlockIds={editedBlockIds}
              activeBlockId={activeBlockId}
              onEditBlock={onEditBlock}
              openSections={openSections}
              onToggleSection={onToggleSection}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function OutlineNode(props: {
  node: ReviewOutlineNode;
  editedBlockIds: Set<string>;
  activeBlockId?: string | null;
  onEditBlock: (block: ReviewBlock) => void;
  openSections: Record<string, boolean>;
  onToggleSection: (id: string, open: boolean) => void;
}) {
  const { node, editedBlockIds, activeBlockId, onEditBlock } = props;

  if (node.kind === 'block') {
    if (!node.block) return null;
    const block = node.block;
    return (
      <LeafItem
        block={block}
        isEdited={editedBlockIds.has(block.id)}
        isActive={activeBlockId === block.id}
        onEdit={() => onEditBlock(block)}
      />
    );
  }

  return <GroupNode {...props} />;
}

interface OutlineTreeProps {
  nodes: ReviewOutlineNode[];
  editedBlockIds: Set<string>;
  activeBlockId?: string | null;
  onEditBlock: (block: ReviewBlock) => void;
  /**
   * Persisted collapse state, keyed by node id. Held by the parent so it
   * survives the panel unmounting while the block editor is open. A missing key
   * means "use the node's defaultOpen".
   */
  openSections: Record<string, boolean>;
  onToggleSection: (id: string, open: boolean) => void;
}

/**
 * Renders a normalized document outline. Model-agnostic: it takes
 * `ReviewOutlineNode[]`, so the same component serves both the LegalDocument
 * tree and the fixed-DOCX paragraph outline.
 */
export function OutlineTree({
  nodes,
  editedBlockIds,
  activeBlockId,
  onEditBlock,
  openSections,
  onToggleSection,
}: OutlineTreeProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation: arrow keys move between focusable items.
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (!listRef.current) return;
    const focusable = Array.from(listRef.current.querySelectorAll<HTMLElement>('button'));
    const idx = focusable.indexOf(globalThis.document.activeElement as HTMLElement);
    if (idx === -1) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusable[Math.min(idx + 1, focusable.length - 1)]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusable[Math.max(idx - 1, 0)]?.focus();
    }
  }, []);

  if (nodes.length === 0) {
    return (
      <p className="text-sm text-globe-grey px-3 py-4">
        {t('GENERATEREVIEW_OUTLINE_EMPTY', { defaultValue: 'No editable content found.' })}
      </p>
    );
  }

  return (
    <div
      ref={listRef}
      role="tree"
      aria-label={t('GENERATEREVIEW_OUTLINE_ARIA', { defaultValue: 'Document outline' })}
      onKeyDown={handleKeyDown}
      className="space-y-1"
    >
      <p className="text-xs text-globe-grey px-3 mb-2">
        {t('GENERATEREVIEW_OUTLINE_HINT', {
          defaultValue: 'Click any block to edit. Arrow keys to navigate.',
        })}
      </p>

      {nodes.map((node) => (
        <OutlineNode
          key={node.id}
          node={node}
          editedBlockIds={editedBlockIds}
          activeBlockId={activeBlockId}
          onEditBlock={onEditBlock}
          openSections={openSections}
          onToggleSection={onToggleSection}
        />
      ))}
    </div>
  );
}
